/**
 * ProfileManager - Manages accessibility profiles
 * 4 profiles: Vision Impaired, Cognitive & Learning, Motor Impaired, Seizure Safe
 */
export default class ProfileManager {
    constructor(widget) {
        this.widget = widget;
        this.activeProfile = null;
        this.profiles = this._defineProfiles();

        this._renderProfileCards();
        this._restoreProfile();
    }

    /**
     * Define profile configurations
     */
    _defineProfiles() {
        return {
            visionImpaired: {
                name: 'profile.vision_impaired',
                desc: 'profile.vision_impaired.desc',
                icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M21 10h-2.5l-1.6-3.2A2 2 0 0 0 15.1 6H8.9a2 2 0 0 0-1.8.8L5.5 10H3a1 1 0 0 0 0 2h2.2a4.5 4.5 0 1 0 8.6 0h1.4a4.5 4.5 0 1 0 8.6 0H21a1 1 0 0 0 0-2zM9.5 15a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm5.6-7H8.9l-.7 1.4h7.6L15.1 8zM18.5 15a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>',
                color: '#1565c0',
                features: {
                    darkHighContrast: true,
                    fontSizing: true,
                    readableFont: true,
                    highlightHeaders: true,
                    enlargeButtons: true,
                    cursorCustomization: true
                }
            },
            cognitive: {
                name: 'profile.cognitive',
                desc: 'profile.cognitive.desc',
                icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93zM4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4zm7-13.93C7.05 1.56 4 4.92 4 9h7V1.07z"/></svg>',
                color: '#2e7d32',
                features: {
                    readableFont: true,
                    fontSizing: true,
                    readingGuide: true,
                    highlightHeaders: true,
                    readableMode: false,
                    textReader: true
                }
            },
            blindness: {
                name: 'profile.blindness',
                desc: 'profile.blindness.desc',
                icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M2.1 3.5 3.5 2.1 21.9 20.5 20.5 21.9l-3.2-3.2A11.9 11.9 0 0 1 12 20C7 20 2.7 16.9 1 12c.8-2 2.2-3.7 4-5l-2.9-2.9zM12 6c5 0 9.3 3.1 11 6-1 2.6-2.9 4.7-5.4 5.8l-1.6-1.6c1.7-.7 3.2-2.1 4.1-4.2-1.7-3.4-5.4-6-8.1-6-1 0-2 .2-3 .5L7.7 5.2C9 4.4 10.5 4 12 4c.9 0 1.7.1 2.6.3L12.9 6A5 5 0 0 0 12 6zm0 4a2 2 0 0 1 2 2c0 .2 0 .4-.1.6l-2.5-2.5c.2-.1.4-.1.6-.1zm-2 2a2 2 0 0 0 2 2c.2 0 .4 0 .6-.1l-2.5-2.5c-.1.2-.1.4-.1.6z"/></svg>',
                color: '#37474f',
                features: {
                    screenReader: true,
                    keyboardNavigation: true,
                    textReader: false,
                    highlightHeaders: false,
                    readingGuide: false
                }
            },
            motorImpaired: {
                name: 'profile.motor_impaired',
                desc: 'profile.motor_impaired.desc',
                icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2zm9 7h-6v13h-2v-6h-2v6H9V9H3V7h18v2z"/></svg>',
                color: '#e65100',
                features: {
                    keyboardNavigation: true,
                    enlargeButtons: true,
                    cursorCustomization: true,
                    highlightElements: true,
                    screenReader: true
                }
            },
            seizureSafe: {
                name: 'profile.seizure_safe',
                desc: 'profile.seizure_safe.desc',
                icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>',
                color: '#6a1b9a',
                features: {
                    muteMedia: true,
                    lowSaturation: true,
                    monochrome: true,
                    readableMode: false
                }
            },
            elder: {
                name: 'profile.elder',
                desc: 'profile.elder.desc',
                icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>',
                color: '#795548',
                features: {
                    fontSizing: true,
                    readableFont: true,
                    enlargeButtons: true,
                    cursorCustomization: true,
                    brightHighContrast: false
                }
            }
        };
    }

    /**
     * Render profile cards into the panel
     */
    _renderProfileCards() {
        const grid = document.querySelector('[data-grid="profiles"]');
        if (!grid) return;

        // Clear existing cards (panel can be recreated on language switch)
        grid.innerHTML = '';

        const profileConfig = this.widget.config.profiles || {};

        for (const [key, profile] of Object.entries(this.profiles)) {
            // Skip disabled profiles (config value can be false, 0, or '0')
            if (profileConfig[key] === false || profileConfig[key] === 0 || profileConfig[key] === '0') continue;

            const card = document.createElement('button');
            card.className = 'accessibility-widget-card profile-card';
            card.dataset.profile = key;
            card.setAttribute('type', 'button');
            card.setAttribute('role', 'switch');
            card.setAttribute('aria-checked', 'false');
            card.setAttribute('aria-label', this.widget.i18n.t(profile.name));
            card.style.setProperty('--profile-color', profile.color);

            // Icon
            const icon = document.createElement('span');
            icon.className = 'card-icon';
            icon.innerHTML = profile.icon;
            card.appendChild(icon);

            // Label
            const label = document.createElement('span');
            label.className = 'card-label';
            label.textContent = this.widget.i18n.t(profile.name);
            card.appendChild(label);

            // Description
            const desc = document.createElement('span');
            desc.className = 'card-desc';
            desc.textContent = this.widget.i18n.t(profile.desc);
            card.appendChild(desc);

            // Click handler
            card.addEventListener('click', () => this.toggleProfile(key));

            grid.appendChild(card);
        }
    }

    /**
     * Refresh profile cards (used after panel refresh, e.g. language switch)
     */
    refresh() {
        this._renderProfileCards();
        this._updateProfileCards();
    }

    /**
     * Toggle a profile on/off
     */
    toggleProfile(key) {
        if (this.activeProfile === key) {
            // Deactivate current profile
            this._deactivateProfile(key);
            this.activeProfile = null;
            this.widget.storage.remove('active_profile');
        } else {
            // Deactivate previous profile
            if (this.activeProfile) {
                this._deactivateProfile(this.activeProfile);
            }
            // Activate new profile
            this._activateProfile(key);
            this.activeProfile = key;
            this.widget.storage.set('active_profile', key);
        }

        this._updateProfileCards();

        // Scroll panel to top after profile selection
        if (this.widget.panel && this.widget.panel.scrollToTop) {
            this.widget.panel.scrollToTop();
        }

        if (typeof this.widget.config.onProfileApply === 'function') {
            this.widget.config.onProfileApply(this.activeProfile);
        }

        this.widget.events.emit('profile:change', { profile: this.activeProfile });
    }

    /**
     * Activate a profile's features
     */
    _activateProfile(key) {
        const profile = this.profiles[key];
        if (!profile) return;

        for (const [featureKey, enabled] of Object.entries(profile.features)) {
            if (enabled) {
                this.widget.toggleFeature(featureKey, true, false);
            }
        }

        // Profile-specific settings
        if (key === 'elder') {
            const f = this.widget.features.fontSizing;
            if (f) {
                f.currentLineSpacing = 3;
                if (typeof f._apply === 'function') f._apply();
                if (typeof f._save === 'function') f._save();
                this.widget.events.emit('fontSizing:change', { key: 'lineSpacing', value: 1 });
            }
        }
    }

    /**
     * Deactivate a profile's features
     */
    _deactivateProfile(key) {
        const profile = this.profiles[key];
        if (!profile) return;

        for (const featureKey of Object.keys(profile.features)) {
            this.widget.toggleFeature(featureKey, false, false);
        }
    }

    /**
     * Update profile card active states
     */
    _updateProfileCards() {
        const cards = document.querySelectorAll('.profile-card');
        cards.forEach(card => {
            const isActive = card.dataset.profile === this.activeProfile;
            card.classList.toggle('active', isActive);
            card.setAttribute('aria-checked', String(isActive));
        });

        // Also update feature cards
        if (this.widget.panel) {
            this.widget.panel.updateAllCards();
        }
    }

    /**
     * Restore saved profile
     */
    _restoreProfile() {
        const saved = this.widget.storage.get('active_profile');
        if (saved && this.profiles[saved]) {
            this._activateProfile(saved);
            this.activeProfile = saved;
            this._updateProfileCards();
        }
    }
}
